import award2023 from '@/assets/award-2023.webp';
import award2024 from '@/assets/award-2024.webp';
import award2025 from '@/assets/award-2025.webp';
import logo from '@/assets/logo.webp';
import work1 from '@/assets/work-1.webp';
import work2 from '@/assets/work-2.webp';
import work3 from '@/assets/work-3.webp';
import work4 from '@/assets/work-4.webp';
import work5 from '@/assets/work-5.webp';
import work6 from '@/assets/work-6.webp';
import work7 from '@/assets/work-7.webp';

/*
 * The public site's words and pictures, in one place.
 *
 * **Everything here is ported, not written — with one marked exception.** The
 * copy is `reference/prototype-copy/public-site.md`, captured off the Airo
 * draft site on 2026-08-04; the images and their alt text are
 * `reference/assets/MANIFEST.md`. ROADMAP.md §8.5: "Port the prototype's copy —
 * it is good and it is the owner's voice." Paraphrasing it would replace a real
 * business's voice with a rebuild's, which is the one thing this slice must not
 * do.
 *
 * The exception is `HOME_HOW_IT_WORKS`, a section the captured site does not
 * have. It carries its own note saying so. **It is the only wording on the
 * public site that was written rather than ported**, and if you are reading
 * this file to find out whether a sentence is the business's or ours, that is
 * the answer.
 *
 * In a data file rather than inline in the components because the owner will
 * want to change wording, and "where is the sentence about the pets" should
 * have one answer.
 *
 * The photographs are re-encoded from `reference/assets/` at 1400px and 82%
 * WebP — 4.5MB of originals down to under 1MB. The full-resolution set stays in
 * `reference/` as the archive, because the Airo site it came from is going away
 * and there is no second source.
 */

/** business-rules.md §1 — "safe to hard-code or seed. All from the public site." */
export const NAP = {
  name: 'Honeycomb Cleaning Services',
  legalName: 'Honeycomb Cleaning Services LLC',
  phone: '(435) 238-3286',
  /** `tel:` needs the digits, not the formatting. */
  phoneHref: 'tel:+14352383286',
  email: 'honeycombcleaningservicesllc@gmail.com',
  city: 'Cedar City',
  state: 'UT',
  stateName: 'Utah',
  postalCode: '84720',
  country: 'US',
  areaServed: 'Cedar City, Utah and surrounding areas',
  hours: 'Available Monday through Saturday',
  instagram: 'https://www.instagram.com/honey.combcleaningservices/',
} as const;

/*
 * **The Facebook link is deliberately absent.** business-rules.md §9, question
 * 2: the two sites link different pages — the GoDaddy site `100087005091976`
 * and the Airo draft site `61571791751839` — and the documented default is
 * "omit the Facebook link rather than link the wrong page". Sending customers
 * to a page the business does not run is worse than not linking at all.
 *
 * The Airo id is evidence rather than an answer: the owner chose that site as
 * the source of the imagery, which is not the same as confirming its social
 * links. Add `facebook` here once they say which.
 */

export const LOGO = { src: logo, alt: 'Honeycomb Cleaning Services logo' } as const;

/** Footer order: 2023 · 2024 · 2025. See `AWARDS_HOME_ORDER` for the other one. */
export const AWARDS = [
  { year: 2023, src: award2023 },
  { year: 2024, src: award2024 },
  { year: 2025, src: award2025 },
] as const;

/*
 * **The home page renders them 2023 · 2025 · 2024**, which is what the live site
 * does. `public-site.md` records it and says "the home-page order looks like a
 * mistake rather than a choice" — so it is carried over rather than silently
 * corrected, and named here so whoever fixes it knows they are changing the
 * site rather than fixing the port.
 */
export const AWARDS_HOME_ORDER = [AWARDS[0], AWARDS[2], AWARDS[1]] as const;

export function awardAlt(year: number): string {
  return `Best of Iron County ${String(year)} Winner — Honeycomb Cleaning Services`;
}

/**
 * The seven work photographs, with the site's own alt text.
 *
 * MANIFEST.md: "Worth carrying over — it is specific and somebody wrote it
 * deliberately." The CDN copies carried generic alt instead ("Professional
 * house cleaning"), and this is the better text.
 *
 * > **Corrected in 8.4 after looking at the photographs.** The manifest's alt
 * > list was right as a *set* and attached to the wrong files — six of the
 * > seven were off. It had `honeycomb cleaning 3` as "dusting a chandelier"
 * > when it is somebody vacuuming stairs, and `4` as "cleaning a decorative
 * > shelf" when it is the team photo. The mapping below is what the images
 * > actually show, checked one by one; `reference/assets/MANIFEST.md` now says
 * > the same.
 * >
 * > Worth being blunt about why this was not a cosmetic error: alt text is what
 * > a blind visitor is *told the page contains*. Confidently wrong alt is worse
 * > than none, because there is nothing about it that reads as missing.
 */
export const WORK_PHOTOS = [
  { src: work1, alt: 'Honeycomb crew member cleaning a bathroom mirror' },
  { src: work2, alt: 'Crew member cleaning a glass shower' },
  { src: work3, alt: 'Crew member vacuuming carpeted stairs' },
  { src: work4, alt: 'The Honeycomb Crew team photo' },
  { src: work5, alt: 'Crew member cleaning a decorative shelf' },
  { src: work6, alt: 'Crew member dusting a chandelier' },
  { src: work7, alt: 'Crew member cleaning hardwood floors in a hallway' },
] as const;

/**
 * Page-slot assignments, per MANIFEST.md's "Used as" column.
 *
 * The slots are by **file** and were unaffected by the alt-text mix-up — and
 * the crew slot reads better than the manifest implied, because
 * `honeycomb cleaning 4` turns out to be the team photograph rather than
 * somebody dusting a shelf.
 */
export const HERO_PHOTO = WORK_PHOTOS[0];
export const SERVICES_PHOTO = WORK_PHOTOS[2];
export const CREW_PHOTO = WORK_PHOTOS[3];

/**
 * The header's page links.
 *
 * > **"My Schedule" used to be here and is not any more** — owner decision,
 * > 2026-08-04. It is what the Airo site's header says, and 8.4 ported it. That
 * > was the wrong call: §8.5's "port the copy, do not paraphrase" protects the
 * > *marketing prose*, the words that are the business's voice. A navigation
 * > label is an affordance, and this one lied in both directions — a visitor
 * > who has never signed in clicked "My Schedule" expecting a calendar and got
 * > a login form, and somebody signed in got a dashboard the label never
 * > mentioned.
 * >
 * > `components/public/AccountMenu.tsx` replaces it: "Sign in" when nobody is,
 * > "My account" with role-appropriate shortcuts when somebody is.
 *
 * The footer keeps **"Client Portal"**, which is also captured wording and is
 * unambiguous — it names its destination.
 */
export const HEADER_NAV = [
  { label: 'Home', to: '/' },
  { label: 'Services', to: '/services' },
  { label: 'Contact Us', to: '/contact' },
] as const;

export const FOOTER_LINKS = [
  { label: 'Home', to: '/' },
  { label: 'Services', to: '/services' },
  { label: 'Contact Us', to: '/contact' },
  { label: 'Careers', to: '/careers' },
  { label: 'Client Portal', to: '/login' },
] as const;

export const FOOTER_BLURB =
  'House cleaning services made easy. We take care of your home with the same care as if it were our own.';

export const COPYRIGHT = '© 2026 Honeycomb Cleaning Services. All Rights Reserved.';

// ---------------------------------------------------------------------------
// Home
// ---------------------------------------------------------------------------

export const HOME = {
  title: 'Honeycomb Cleaning Services – Best of Iron County 2023, 2024 & 2025 | Cedar City, UT',
  description:
    'Award-winning house cleaning in Cedar City, Utah. Voted Best of Iron County 3 years in a row (2023, 2024, 2025). Eco-friendly products, trusted team. Call (435) 238-3286.',

  hero: {
    heading: 'House Cleaning Services Made Easy!',
    body: 'We take care of your home with the same care as if it were our own. Serving Cedar City, Utah and surrounding areas.',
    cta: 'Get a Quote',
  },

  whatWeDo: {
    heading: 'What We Do',
    sub: 'Comprehensive cleaning solutions for every need',
    cards: [
      {
        title: 'Residential Services',
        body: "Don't stress about your pets or children: our environmentally friendly products provide a safe clean. We take care of your home with the same care as if it were our own. We leave your sanctuary spotless so that you can focus on the rest of your life.",
      },
      {
        title: 'Deep Clean Services',
        body: 'If you have your day to day cleaning under control, let us do the really dirty work. We provide deep house cleaning services to reach the places you might have missed. We have a variety of bonus services from oven cleaning to air vents.',
      },
      {
        title: 'Clean = Safe',
        body: 'We are using up to date health agency guidelines to be sure our procedures limit the spread of disease. These procedures include using EPA-approved cleaning agents to disinfect all high-touch areas and safety measures for all employees.',
      },
    ],
  },

  whyChoose: {
    heading: 'Why Choose Honeycomb?',
    items: [
      {
        title: 'Eco-Friendly',
        body: 'Environmentally friendly products safe for your family and pets.',
      },
      {
        title: 'EPA-Approved',
        body: 'We use EPA-approved agents to disinfect all high-touch areas.',
      },
      { title: 'Trusted Team', body: 'Our professional crew treats your home like their own.' },
    ],
  },

  awards: {
    eyebrow: 'AWARD-WINNING SERVICE',
    heading: 'Best of Iron County — 3 Years Running',
    body: "Voted the best cleaning service in Iron County by the local community three years in a row. We're proud to serve Cedar City and the surrounding area with the quality you deserve.",
  },

  crew: {
    heading: 'The Honeycomb Crew',
    body: [
      'A good cleaning can transform your home and relieve stress. Our process is about transparency, so we provide constant communication with our customers until the job is done.',
      "If you have questions or special requests, just drop us a line. We're available Monday through Saturday to serve you.",
    ],
    cta: 'Get a Free Quote',
  },

  work: { eyebrow: 'OUR WORK', heading: 'See the Honeycomb Difference' },

  closing: {
    heading: 'Ready for a Spotless Home?',
    body: 'Call us or request a free quote today.',
    cta: 'Request a Quote',
  },
} as const;

/**
 * **The one block on this site that was written rather than ported.**
 *
 * Everything else in this file comes from
 * `reference/prototype-copy/public-site.md`. This section is **not on the
 * captured site at all**, so there was nothing to port — it was asked for
 * (slice 8.11) because cleaning is a stranger-in-your-house purchase and the
 * hesitation is about process rather than price.
 *
 * **Approved 2026-08-05**, having shipped as `HOME_HOW_IT_WORKS_DRAFT` for a
 * day. Worth knowing what that approval is and is not: it clears the words to
 * go live, and **the business owner has not read them yet** — they are to look
 * at it later. So this is settled enough to ship and still the first thing to
 * change if they want it different. It stays flagged in `PROGRESS.md` §Needed
 * from the owner for that reason.
 *
 * **This is 8.8's near-miss, done on purpose instead of by accident.** That
 * slice added a heading and a lede over the Services grid to give it a centred
 * intro, which was inventing copy on a page whose whole rule is that the copy
 * is ported, and it was removed. The difference here is not that the rule
 * changed — it is that a section was asked for that exists nowhere to port
 * from, and the draft was shown before it shipped rather than after.
 *
 * Three of the four steps deliberately lean on wording that *is* captured, so
 * the voice is the business's even while the sentences are not:
 *
 *   - "just drop us a line" — the Crew and Contact blocks
 *   - "available Monday through Saturday" — the footer and the Contact block
 *   - "treats your home like their own" — Why Choose Honeycomb
 *
 * **Nothing here promises a turnaround time, a price, a guarantee, an insurance
 * status or a refund**, and there is a test holding that. A sentence in this
 * block is free to be reworded; it is not free to commit the business to
 * something nobody at Honeycomb has said. If their own wording *does* make a
 * commitment, that is theirs to make and the test's list is theirs to shorten.
 */
export const HOME_HOW_IT_WORKS = {
  eyebrow: 'HOW IT WORKS',
  heading: 'From Quote to Clean',
  lede: 'Four steps, start to finish — and nothing you have to chase.',

  steps: [
    {
      title: 'Ask for a quote',
      body: "Tell us about the property and what you'd like cleaned. It's free, and it doesn't commit you to anything.",
    },
    {
      title: 'We come back to you',
      body: "We'll confirm what it costs and answer anything you want to ask first. If you have questions or special requests, just drop us a line.",
    },
    {
      title: 'Pick a day',
      body: "One clean, or a regular schedule that suits you. We're available Monday through Saturday.",
    },
    {
      title: 'We clean',
      body: 'Our crew treats your home like their own, and we stay in touch until the job is done.',
    },
  ],
} as const;

// ---------------------------------------------------------------------------
// Services
// ---------------------------------------------------------------------------

export const SERVICES = {
  title: 'Our Services – Honeycomb Cleaning Services | Cedar City, UT',
  description:
    'Residential, commercial and event cleaning in Cedar City, Utah. Bedrooms, bathrooms, living areas, kitchens, offices and more. Call (435) 238-3286 for a free quote.',

  eyebrow: 'WHAT WE OFFER',
  heading: 'One Call Cleans It All',
  subheading: 'Services',
  sub: 'Everything your home or business needs, covered.',

  /*
   * **These are the site's six lists, and four of them are rooms rather than
   * property types.** public-site.md is explicit that Bedrooms, Bathroom,
   * Living Areas and Kitchens are *not* `property_category` rows — a category
   * is a kind of property and those are rooms inside one. Rendering them here
   * as marketing copy is right; seeding them as categories would not be.
   */
  groups: [
    {
      title: 'Bedrooms',
      items: [
        'Dust all surfaces and mirrors',
        'Sweep, mop, vacuum',
        'Tidying and organization',
        'Trash and recycling',
      ],
    },
    {
      title: 'Bathroom',
      items: [
        'Dust all surfaces',
        'Sweep and mop',
        'Clean toilets, showers and sinks',
        'Clean all mirrors',
      ],
    },
    {
      title: 'Living Areas',
      items: [
        'Dust all surfaces',
        'Sweep, vacuum, mop',
        'Organization',
        'Trash and recycling',
        'Change linens',
      ],
    },
    {
      title: 'Kitchens',
      items: [
        'Dust surfaces',
        'Sweep and mop',
        'Dish washing and put away',
        'Sanitize appliances',
        'Trash and recycling',
      ],
    },
    {
      title: 'Commercial',
      items: [
        'Bars and restaurants',
        'Hotels / motels',
        'Office buildings',
        'Hospitals',
        'Apartment buildings',
        'Retail stores',
      ],
    },
    {
      title: 'Events',
      items: [
        'Weddings and birthday parties',
        'Special event preparations',
        'Corporate and office parties',
      ],
    },
  ],

  closing: {
    heading: 'Ready to Get Started?',
    body: 'Contact us today for a free estimate.',
    cta: 'Contact Us',
  },
} as const;

// ---------------------------------------------------------------------------
// Contact
// ---------------------------------------------------------------------------

export const CONTACT = {
  title: 'Contact Us – Honeycomb Cleaning Services | Cedar City, UT',
  description:
    'Get a free cleaning quote in Cedar City, Utah. Tell us about your properties and we will get back to you. Call (435) 238-3286.',

  eyebrow: 'REACH OUT',
  heading: 'How Can We Help?',
  subheading: 'Get in Touch',
  body: 'A good cleaning can transform your home and relieve stress. Our process is about transparency, so we provide constant communication with our customers until the job is done. If you have questions or special requests, just drop us a line.',

  details: [
    { label: 'PHONE', value: NAP.phone },
    { label: 'EMAIL', value: NAP.email },
    { label: 'LOCATION', value: 'Cedar City, Utah, United States' },
    { label: 'HOURS', value: 'Available Monday through Saturday' },
    { label: '', value: 'Email or call for current openings' },
  ],
} as const;

// ---------------------------------------------------------------------------
// Careers
// ---------------------------------------------------------------------------

export const CAREERS = {
  title: 'Careers — Honeycomb Cleaning Services',
  description:
    'Join the Honeycomb Cleaning Services team in Cedar City, Utah. We are looking for reliable, detail-oriented people who take pride in their work.',

  eyebrow: 'Now Hiring',
  heading: 'Join the Honeycomb Team',
  body: "We're looking for reliable, detail-oriented people who take pride in their work. Fill out the application below and we'll be in touch.",
  requiredNote: 'Fields marked with * are required.',
} as const;
